import { useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useIntl } from 'react-intl';

import Spacings from '@dynatrace/strato-design-tokens/spacings';
import { ZoomInIcon, ZoomOutIcon } from '@dynatrace/strato-icons';

import { useGridPlotSize } from '../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { ChartToolbarRenderer } from '../../../core/components/toolbar/ChartToolbarRenderer.js';
import { useTriggerZoom } from '../../../core/components/zoom/hooks/useTriggerZoom.js';
import { useA11yToolbarTabIndex } from '../../../core/hooks/useA11yToolbarTabIndex.js';
import { usePreventScrollOnWheel } from '../../../core/hooks/usePreventScrollOnWheel.js';
import { ZOOM_ACTIONS, ZoomInZoomOut } from '../../../core/types/zoom.js';
import { getKeyShortcutByAgent } from '../../../core/utils/get-key-shortcut-by-agent.js';
import { useZoomState } from '../../hooks/store/selectors.js';
import { useTimeseriesOverlayRef } from '../../hooks/useTimeseriesOverlayRef.js';
import { TIMESERIES_MESSAGES } from '../../messages/tooltips-text.js';
import { useApplyZoomDelta } from '../overlays/hooks/useApplyZoomDelta.js';

const controlStyle = { marginRight: Spacings.Size2 };

export const ZoomInOutButtons = () => {
  const { zoomInEnabled, zoomOutEnabled } = useZoomState();
  const zoomInButtonRef = useRef<HTMLButtonElement>(null);
  const zoomOutButtonRef = useRef<HTMLButtonElement>(null);
  const tabIndexIn = useA11yToolbarTabIndex(ZOOM_ACTIONS.ZoomIn);
  const tabIndexOut = useA11yToolbarTabIndex(ZOOM_ACTIONS.ZoomOut);
  const overlayRef = useTimeseriesOverlayRef();
  const intl = useIntl();

  const { width } = useGridPlotSize();
  const applyZoom = useApplyZoomDelta();
  const triggerZoom = useTriggerZoom(width, applyZoom);

  const zoomInShortcut =
    getKeyShortcutByAgent('CONTROL') +
    `+${intl.formatMessage(TIMESERIES_MESSAGES.zoomActionArrowUp)}`;
  const zoomOutShortcut =
    getKeyShortcutByAgent('CONTROL') +
    `+${intl.formatMessage(TIMESERIES_MESSAGES.zoomActionArrowDown)}`;

  usePreventScrollOnWheel(overlayRef);

  /** Handles the zoom when a 'enter' keyboard event happens.
   * @param event - A keyboard event param.
   * @param zoomBehavior - zoom-in or zoom-out interaction.
   */
  const handleZoomKeyboardMode = (
    event: ReactKeyboardEvent,
    zoomBehavior: ZoomInZoomOut,
  ) => {
    if (event.code === 'Enter') {
      triggerZoom(zoomBehavior);
    }
  };

  return (
    <>
      <ChartToolbarRenderer.Control
        ref={zoomInButtonRef}
        prefixIcon={<ZoomInIcon />}
        tooltipText={intl.formatMessage(TIMESERIES_MESSAGES.zoomActionZoomIn)}
        tooltipKeyShortcut={zoomInShortcut}
        testId="chart-toolbar-zoom-in"
        onKeyDown={(event) =>
          handleZoomKeyboardMode(event, ZOOM_ACTIONS.ZoomIn)
        }
        onClick={() => triggerZoom(ZOOM_ACTIONS.ZoomIn)}
        tabIndex={tabIndexIn}
        disabled={!zoomInEnabled}
        divider="left"
        style={controlStyle}
      />
      <ChartToolbarRenderer.Control
        ref={zoomOutButtonRef}
        prefixIcon={<ZoomOutIcon />}
        tooltipText={intl.formatMessage(TIMESERIES_MESSAGES.zoomActionZoomOut)}
        tooltipKeyShortcut={zoomOutShortcut}
        testId="chart-toolbar-zoom-out"
        onKeyDown={(event) =>
          handleZoomKeyboardMode(event, ZOOM_ACTIONS.ZoomOut)
        }
        onClick={() => triggerZoom(ZOOM_ACTIONS.ZoomOut)}
        tabIndex={tabIndexOut}
        disabled={!zoomOutEnabled}
        style={controlStyle}
      />
    </>
  );
};

ZoomInOutButtons['displayName'] = 'ZoomInOutButtons';
